/**
 * LogoUploader — the click-to-upload affordance on the Brand card.
 *
 * Mirrors `<AvatarUploader>` exactly — both consume `useS3Upload` for the
 * three-phase flow (request presigned URL → direct PUT to S3 → confirm).
 * What differs: the visual chip (64×64 logo with company initials),
 * allowed types (SVG ok for logos), size cap (1 MiB).
 *
 * The visual chip is a 64×64 rounded square (matches the prototype's
 * `.logo-preview`). Initials fall back when no logo is set; the camera
 * chip overlay tells users it's tappable.
 */
import * as React from "react";
import { Camera, Building2 } from "lucide-react";
import { cn, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { useS3Upload, type S3UploadStatus } from "../../../lib/use-s3-upload.ts";
import { deriveInitials } from "../../../lib/format.ts";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"] as const;
const MAX_BYTES = 1 * 1024 * 1024; // 1 MiB — must match server (LOGO_MAX_BYTES).

export type LogoUploadStatus = S3UploadStatus;

export interface LogoUploaderProps {
  /** Current public URL (or null when no logo set). */
  currentUrl: string | null;
  /** Initials shown when no logo is set. e.g. company → "SI". */
  initials: string;
  /** Optional callback for the host to render status text below. */
  onStatusChange?: (status: LogoUploadStatus) => void;
  /** When true, the picker button is disabled — the host has view-only rights. */
  readOnly?: boolean;
}

export function LogoUploader({
  currentUrl,
  initials,
  onStatusChange,
  readOnly = false,
}: LogoUploaderProps) {
  const requestUpload = trpc.companies.requestLogoUpload.useMutation();
  const confirm = trpc.companies.confirmLogo.useMutation();
  const utils = trpc.useUtils();

  const { isBusy, openPicker, inputRef, inputProps } = useS3Upload({
    request: requestUpload as never,
    confirm,
    allowedTypes: ALLOWED_TYPES,
    maxBytes: MAX_BYTES,
    // Refetch the company so the form and any document preview using the
    // logo pick up the new URL immediately after confirm.
    invalidateQueries: [],
    onSuccess: async () => {
      await utils.companies.get.invalidate();
      // Top-right toast — the button chip swap is easy to miss and the
      // SaveBar correctly stays "No changes yet" (logo saves through its
      // own two-phase flow, not the text-fields diff). Toast is the loud
      // confirmation that the upload committed.
      toast.success("Logo saved", {
        description: "Your new company logo is live on customer-facing documents.",
      });
    },
    copy: {
      invalidType: "Use a JPEG, PNG, WebP, or SVG image.",
      tooLarge: "Logo must be under 1 MB.",
    },
    ...(onStatusChange ? { onStatusChange } : {}),
  });

  // Reset the image-error gate when the URL changes — a new upload
  // deserves a fresh load attempt.
  const [imgErrored, setImgErrored] = React.useState(false);
  React.useEffect(() => {
    setImgErrored(false);
  }, [currentUrl]);

  const showImage = !!currentUrl && !imgErrored;
  const initialsText = initials || deriveInitials("");

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        disabled={isBusy || readOnly}
        aria-label="Change company logo"
        className={cn(
          "group relative inline-block rounded-md",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
          isBusy && "opacity-70",
        )}
      >
        <div
          // 64x64 square logo preview. Slate fallback bg matches the
          // workspace card / avatar pattern so the empty state reads as
          // "Flint identity" not "missing data."
          className="relative inline-grid place-items-center h-16 w-16 rounded-md bg-slate-700 text-paper font-bold tracking-tight select-none shrink-0 overflow-hidden border border-line-2"
          aria-hidden
        >
          {showImage ? (
            <img
              src={currentUrl ?? undefined}
              alt=""
              loading="lazy"
              decoding="async"
              onError={() => setImgErrored(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : initialsText && initialsText !== "?" ? (
            <span className="text-base">{initialsText}</span>
          ) : (
            <Building2 size={20} strokeWidth={2} aria-hidden />
          )}
        </div>
        <span
          className="absolute -bottom-1 -right-1 inline-grid place-items-center h-6 w-6 rounded-sm bg-ink text-paper opacity-80 group-hover:opacity-100 transition-opacity shadow-card ring-2 ring-paper"
          aria-hidden
        >
          <Camera size={11} strokeWidth={2.5} />
        </span>
      </button>

      <input ref={inputRef} className="sr-only" aria-label="Company logo file" {...inputProps} />
    </>
  );
}

/**
 * Convenience: turn a `LogoUploadStatus` into the line of helper text the
 * Brand-card body renders below the logo. Mirrors `avatarStatusMessage`.
 */
export function logoStatusMessage(status: LogoUploadStatus): {
  text: string;
  tone: "muted" | "bad";
} {
  if (status.kind === "uploading") {
    return {
      text:
        status.phase === "request"
          ? "Preparing upload…"
          : status.phase === "put"
            ? "Uploading logo…"
            : "Saving…",
      tone: "muted",
    };
  }
  if (status.kind === "error") {
    return { text: status.message, tone: "bad" };
  }
  if (status.kind === "saved") {
    return { text: "Logo saved · just now", tone: "muted" };
  }
  return {
    text: "PNG, SVG, JPEG, or WebP · up to 1 MB · 1:1 ratio recommended",
    tone: "muted",
  };
}
